import { Plane, Check, X, Calendar, Users, MapPin, Clock, Globe, Tag, ArrowRight } from 'lucide-react';
import { useTranslation } from '../../../i18n/i18n';

const FlightView = ({ product, lang }) => {
    const { t } = useTranslation(lang);

    const getFieldValue = (fieldArray, fallback = '') => {
        if (!fieldArray || fieldArray.length === 0) return fallback;
        const value = fieldArray[0][lang] || fieldArray[0].en || fieldArray[0].fr || fieldArray[0].es;
        return value || fallback;
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('notSpecified');
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US');
        } catch {
            return dateString;
        }
    };

    const getFlightName = () => {
        return product[lang] || product.en || product.fr || product.es || t('flight');
    };

    const styles = {
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '12px 24px 24px',
            marginBottom: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
        },
        infoCard: {
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        infoItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f3f4f6'
        },
        infoLabel: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
        },
        infoValue: {
            color: '#6b7280',
            fontSize: '14px',
            textAlign: 'right'
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: 0
        },
        listItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 0',
            fontSize: '14px',
            color: '#374151'
        },
        iconText: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        twoColumn: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
        },
        statCard: {
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#7c3aed',
            marginBottom: '4px'
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            textTransform: 'uppercase'
        },
        itineraryGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px',
            marginTop: '16px'
        },
        itineraryCard: {
            background: '#f0f9ff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #bae6fd',
            position: 'relative'
        },
        itineraryHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            paddingBottom: '12px',
            borderBottom: '1px solid #dbeafe'
        },
        airlineInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        airlineName: {
            fontWeight: '600',
            color: '#1f2937'
        },
        flightNumber: {
            fontSize: '12px',
            color: '#6b7280',
            background: '#e5e7eb',
            padding: '2px 6px',
            borderRadius: '4px'
        },
        routeInfo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
        },
        airportCode: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1e40af'
        },
        airportName: {
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '4px'
        },
        durationInfo: {
            textAlign: 'center',
            padding: '0 16px'
        },
        duration: {
            fontSize: '14px',
            color: '#374151',
            fontWeight: '500'
        },
        arrowContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af'
        },
        timeInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '12px'
        },
        timeItem: {
            textAlign: 'center'
        },
        timeLabel: {
            fontSize: '11px',
            color: '#6b7280',
            textTransform: 'uppercase'
        },
        timeValue: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: '#9ca3af',
            fontStyle: 'italic'
        },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase'
        },
        directBadge: {
            background: '#10b981',
            color: 'white'
        },
        connectingBadge: {
            background: '#f59e0b',
            color: 'white'
        },
        cabinClassGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginTop: '16px'
        },
        cabinClassCard: {
            background: '#f0fdf4',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #bbf7d0',
            textAlign: 'center'
        },
        cabinClassName: {
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '4px'
        },
        cabinClassPrice: {
            fontSize: '14px',
            color: '#10b981',
            fontWeight: '500'
        }
    };

    // Données mockées pour le vol (à remplacer par les vraies données API)
    const flightData = product.flight || {
        id: product.id,
        airline_name: getFlightName(),
        airline_code: 'SU', // Code IATA
        flight_number: 'SU2456',
        aircraft_type: 'A320',
        cabin_classes: [
            { id: 1, name: t('economyClass'), price: 250, baggage_allowance: '23kg' },
            { id: 2, name: t('businessClass'), price: 650, baggage_allowance: '32kg + 2 bagages cabine' }
        ],
        itineraries: [
            {
                id: 1,
                departure_airport_code: 'CDG',
                departure_airport_name: 'Paris Charles de Gaulle',
                departure_terminal: '2E',
                departure_time: '08:30',
                arrival_airport_code: 'JFK',
                arrival_airport_name: 'New York John F. Kennedy',
                arrival_terminal: '4',
                arrival_time: '11:15',
                duration: '7h45m',
                is_direct: true,
                stopovers: []
            }
        ],
        baggage_allowance: {
            cabin: '1 bagage + 1 accessoire',
            checked: '23kg par passager',
            additional_fees: 'Frais supplémentaires pour excédent'
        },
        services_included: [
            t('mealService'),
            t('entertainment'),
            t('wifi'),
            t('seatSelection')
        ],
        restrictions: [
            t('noPets'),
            t('noSmoking'),
            t('liquidsLimit')
        ],
        checkin_info: {
            online_checkin: '24h avant le départ',
            airport_checkin: '3h avant le départ',
            boarding_time: '45min avant le départ'
        }
    };

    return (
        <>
            {/* Section Détails du vol */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <Plane size={20} />
                    {t('flightDetails')}
                </h2>

                {/* Statistiques rapides */}
                <div style={styles.statsGrid}>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{flightData.airline_code}</div>
                        <div style={styles.statLabel}>{t('airlineCode')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{flightData.itineraries?.length || 0}</div>
                        <div style={styles.statLabel}>{t('segments')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{flightData.cabin_classes?.length || 0}</div>
                        <div style={styles.statLabel}>{t('cabinClasses')}</div>
                    </div>
                </div>

                <div style={styles.grid}>
                    {/* Informations de base */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('basicInfo')}</h3>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('airline')}</span>
                            <span style={styles.infoValue}>{flightData.airline_name}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('flightNumber')}</span>
                            <span style={styles.infoValue}>{flightData.flight_number}</span>
                        </div>
                        {flightData.aircraft_type && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('aircraftType')}</span>
                                <span style={styles.infoValue}>{flightData.aircraft_type}</span>
                            </div>
                        )}
                    </div>

                    {/* Informations de bagages */}
                    {flightData.baggage_allowance && (
                        <div style={styles.infoCard}>
                            <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('baggageAllowance')}</h3>
                            {flightData.baggage_allowance.cabin && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('cabinBaggage')}</span>
                                    <span style={styles.infoValue}>{flightData.baggage_allowance.cabin}</span>
                                </div>
                            )}
                            {flightData.baggage_allowance.checked && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('checkedBaggage')}</span>
                                    <span style={styles.infoValue}>{flightData.baggage_allowance.checked}</span>
                                </div>
                            )}
                            {flightData.baggage_allowance.additional_fees && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('additionalFees')}</span>
                                    <span style={styles.infoValue}>{flightData.baggage_allowance.additional_fees}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Informations d'enregistrement */}
                    {flightData.checkin_info && (
                        <div style={styles.infoCard}>
                            <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('checkinInfo')}</h3>
                            {flightData.checkin_info.online_checkin && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('onlineCheckin')}</span>
                                    <span style={styles.infoValue}>{flightData.checkin_info.online_checkin}</span>
                                </div>
                            )}
                            {flightData.checkin_info.airport_checkin && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('airportCheckin')}</span>
                                    <span style={styles.infoValue}>{flightData.checkin_info.airport_checkin}</span>
                                </div>
                            )}
                            {flightData.checkin_info.boarding_time && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>{t('boardingTime')}</span>
                                    <span style={styles.infoValue}>{flightData.checkin_info.boarding_time}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Itinéraires */}
                {flightData.itineraries && flightData.itineraries.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('itinerary')}</h3>
                        <div style={styles.itineraryGrid}>
                            {flightData.itineraries.map((itinerary, index) => (
                                <div key={index} style={styles.itineraryCard}>
                                    <div style={styles.itineraryHeader}>
                                        <div style={styles.airlineInfo}>
                                            <Plane size={16} />
                                            <span style={styles.airlineName}>{flightData.airline_name}</span>
                                            <span style={styles.flightNumber}>{flightData.flight_number}</span>
                                        </div>
                                        {itinerary.is_direct ? (
                                            <span style={{ ...styles.badge, ...styles.directBadge }}>
                                                {t('directFlight')}
                                            </span>
                                        ) : (
                                            <span style={{ ...styles.badge, ...styles.connectingBadge }}>
                                                {t('connectingFlight')}
                                            </span>
                                        )}
                                    </div>

                                    <div style={styles.routeInfo}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={styles.airportCode}>{itinerary.departure_airport_code}</div>
                                            <div style={styles.airportName}>
                                                {itinerary.departure_airport_name}
                                                {itinerary.departure_terminal && ` • Terminal ${itinerary.departure_terminal}`}
                                            </div>
                                        </div>

                                        <div style={styles.arrowContainer}>
                                            <ArrowRight size={20} />
                                            <div style={styles.durationInfo}>
                                                <div style={styles.duration}>{itinerary.duration}</div>
                                            </div>
                                        </div>

                                        <div style={{ textAlign: 'center' }}>
                                            <div style={styles.airportCode}>{itinerary.arrival_airport_code}</div>
                                            <div style={styles.airportName}>
                                                {itinerary.arrival_airport_name}
                                                {itinerary.arrival_terminal && ` • Terminal ${itinerary.arrival_terminal}`}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={styles.timeInfo}>
                                        <div style={styles.timeItem}>
                                            <div style={styles.timeLabel}>{t('departure')}</div>
                                            <div style={styles.timeValue}>{itinerary.departure_time}</div>
                                        </div>
                                        <div style={styles.timeItem}>
                                            <div style={styles.timeLabel}>{t('arrival')}</div>
                                            <div style={styles.timeValue}>{itinerary.arrival_time}</div>
                                        </div>
                                    </div>

                                    {itinerary.stopovers && itinerary.stopovers.length > 0 && (
                                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                                                {t('stopovers')}: {itinerary.stopovers.length}
                                            </div>
                                            {itinerary.stopovers.map((stopover, idx) => (
                                                <div key={idx} style={{
                                                    fontSize: '11px',
                                                    color: '#374151',
                                                    padding: '4px 8px',
                                                    background: '#f3f4f6',
                                                    borderRadius: '4px',
                                                    marginBottom: '4px'
                                                }}>
                                                    {stopover.airport_code} ({stopover.duration})
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Classes de cabine */}
                {flightData.cabin_classes && flightData.cabin_classes.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('cabinClasses')}</h3>
                        <div style={styles.cabinClassGrid}>
                            {flightData.cabin_classes.map((cabin, index) => (
                                <div key={index} style={styles.cabinClassCard}>
                                    <div style={styles.cabinClassName}>{cabin.name}</div>
                                    {cabin.price && (
                                        <div style={styles.cabinClassPrice}>
                                            {cabin.price}€
                                        </div>
                                    )}
                                    {cabin.baggage_allowance && (
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                            {cabin.baggage_allowance}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Services inclus et restrictions */}
                <div style={{ marginTop: '24px' }}>
                    <div style={styles.grid}>
                        {/* Services inclus */}
                        {flightData.services_included && flightData.services_included.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('servicesIncluded')}</h3>
                                <div style={styles.infoCard}>
                                    <ul style={styles.list}>
                                        {flightData.services_included.map((service, index) => (
                                            <li key={index} style={styles.listItem}>
                                                <Check color="#10b981" size={16} />
                                                {service}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Restrictions */}
                        {flightData.restrictions && flightData.restrictions.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('restrictions')}</h3>
                                <div style={styles.infoCard}>
                                    <ul style={styles.list}>
                                        {flightData.restrictions.map((restriction, index) => (
                                            <li key={index} style={styles.listItem}>
                                                <X color="#ef4444" size={16} />
                                                {restriction}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Inclus/Exclus */}
            {(product.included && product.included.length > 0) ||
                (product.excluded && product.excluded.length > 0) ? (
                <div style={styles.section}>
                    <div style={styles.twoColumn}>
                        {product.included && product.included.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#10b981' }}>{t('included')}</h3>
                                <ul style={styles.list}>
                                    {product.included.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <Check color="#10b981" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {product.excluded && product.excluded.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#ef4444' }}>{t('excluded')}</h3>
                                <ul style={styles.list}>
                                    {product.excluded.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <X color="#ef4444" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Section Éléments requis/interdits */}
            {(product.required_items && product.required_items.length > 0) ||
                (product.prohibited_items && product.prohibited_items.length > 0) ? (
                <div style={styles.section}>
                    <div style={styles.twoColumn}>
                        {product.required_items && product.required_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#10b981' }}>{t('requiredItems')}</h3>
                                <ul style={styles.list}>
                                    {product.required_items.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <Check color="#10b981" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {product.prohibited_items && product.prohibited_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#ef4444' }}>{t('prohibitedItems')}</h3>
                                <ul style={styles.list}>
                                    {product.prohibited_items.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <X color="#ef4444" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default FlightView;